import { cn } from "@/libs/utils/tailwind/cn";
import { ServiceSearchResult } from "@/hooks/queries/useServiceSearchQuery";
import Spinner from "@/components/ui/Spinner";
import { highlightText } from "../utils/highlightText";

export default function SearchDropdown({
  services,
  isFetching,
  selectedIndex,
  inputValue,
  itemRefs,
  onServiceSelect,
  onMouseEnter,
}: SearchDropdownProps) {
  const hasResults = services.length > 0;

  return (
    <div
      className="absolute top-full left-0 right-0 mt-1 bg-white rounded-2xl shadow-lg border border-default-200 max-h-36 sm:max-h-72 overflow-y-auto z-50"
      role="listbox"
      aria-label="Service search results"
    >
      {/* Loading indicator - always first row when fetching */}
      {isFetching && (
        <div className="flex items-center gap-2 px-6 py-3 border-b border-default-200 rounded-t-2xl">
          <Spinner
            classNames={{
              svg: "h-4 w-4",
            }}
            aria-label="Loading search results"
          />
          <span className="text-default-400 text-sm">Searching...</span>
        </div>
      )}
      {/* Show old results while loading or new results when loaded */}
      {hasResults
        ? services.map((service, index) => (
            <div
              key={service.id}
              ref={(el) => {
                itemRefs.current[index] = el;
              }}
              role="option"
              aria-selected={selectedIndex === index}
              className={cn(
                "px-6 py-3 cursor-pointer transition-colors last:rounded-b-2xl",
                // Only round top if spinner is not shown
                !isFetching && index === 0 && "first:rounded-t-2xl",
                selectedIndex === index
                  ? "bg-primary-50"
                  : "hover:bg-default-100"
              )}
              onMouseEnter={() => onMouseEnter(index)}
              onClick={() => onServiceSelect(service)}
            >
              <div className="text-left text-sm font-normal">
                {highlightText(service.service_name, inputValue)}
              </div>
            </div>
          ))
        : !isFetching && (
            <div className="px-6 py-4 text-default-400 text-sm rounded-t-2xl">
              No services found
            </div>
          )}
    </div>
  );
}

interface SearchDropdownProps {
  services: ServiceSearchResult[];
  isFetching: boolean;
  selectedIndex: number;
  inputValue: string;
  itemRefs: React.MutableRefObject<(HTMLDivElement | null)[]>;
  onServiceSelect: (service: ServiceSearchResult) => void;
  /** we need this to set the selected index when the mouse enters a dropdown item */
  onMouseEnter: (index: number) => void;
}
